import React from "react";
import { format } from "date-fns";
import { Box, Typography, Chip, Tooltip, Stack, useTheme } from "@mui/material";
import {
  CopyAll,
  Delete,
  EditOutlined,
  PlayArrow,
  BuildOutlined,
} from "@mui/icons-material";
import { Tables } from "../../../database.types";
import { useNavigate } from "react-router-dom";
import { StandardButton } from "../../components/StandardButton";
import { useSupabase } from "../../auth/useSupabase";
import { App } from "../../types/types";

interface GameListItemProps {
  game: App.GameWithCreator;
  teamOne: Tables<"teams"> | null;
  teamTwo: Tables<"teams"> | null;
  winningTeamName?: string;
  onCopyLink: () => void;
  onDelete: () => void;
  onStart: () => void;
}

const GameStatusBadge: React.FC<{
  game: App.GameWithCreator;
  winningTeamName?: string;
}> = ({ game, winningTeamName }) => {
  if (game.winning_team_id && winningTeamName) {
    return (
      <Chip
        label={`Winner: ${winningTeamName}`}
        size="small"
        color="success"
        sx={{ fontWeight: "medium" }}
      />
    );
  }
  if (game.is_started) {
    return (
      <Chip
        label="In Progress"
        size="small"
        color="primary"
        variant="filled"
      />
    );
  }
  if (!game.config_complete) {
    return (
      <Chip
        label="Setup Incomplete"
        size="small"
        color="error"
        variant="filled"
      />
    );
  } else if (game.played) {
    return (
      <Chip
        label="Completed"
        size="small"
        color="success"
        sx={{ fontWeight: "medium" }}
      />
    );
  } else {
    return (
      <Chip
        label="Ready to Play"
        size="small"
        color="info"
        sx={{ fontWeight: "medium" }}
      />
    );
  }
};

const GameListItem: React.FC<GameListItemProps> = ({
  game,
  teamOne,
  teamTwo,
  winningTeamName,
  onCopyLink,
  onDelete,
  onStart,
}) => {
  const nav = useNavigate();
  const theme = useTheme();
  const { user } = useSupabase();

  const userIsCreator = user?.id === game.creator_id;

  const accentColor = game.config_complete
    ? game.played
      ? theme.palette.success.main
      : theme.palette.info.main
    : theme.palette.warning.main;

  const handleGameClick = () => {
    if (!userIsCreator) return;
    if (game.config_complete) {
      if (!game.is_started) {
        onStart();
      } else {
        nav(`/game/${game.id}/admin`);
      }
    } else {
      nav(`/game/${game.id}/builder`);
    }
  };

  const primaryAction = () => {
    if (!userIsCreator) return null;

    if (!game.config_complete) {
      return (
        <StandardButton
          variant="contained"
          startIcon={<BuildOutlined />}
          size="medium"
          onClick={(e) => {
            e.stopPropagation();
            nav(`/game/${game.id}/builder`);
          }}
        >
          Finish Setup
        </StandardButton>
      );
    }

    if (!game.is_started) {
      return (
        <StandardButton
          variant="contained"
          startIcon={<PlayArrow />}
          size="medium"
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
        >
          Start
        </StandardButton>
      );
    }

    if (game.is_started && !game.played) {
      return (
        <StandardButton
          variant="contained"
          size="medium"
          onClick={(e) => {
            e.stopPropagation();
            nav(`/game/${game.id}/admin`);
          }}
        >
          Resume
        </StandardButton>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        transition: "all 0.2s",
        cursor: userIsCreator ? "pointer" : undefined,
        "&:hover": {
          boxShadow: 2,
          borderColor: accentColor,
        },
      }}
      onClick={handleGameClick}
      data-testid={`game-list-item-${game.name}`}
    >
      {/* Game Info */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Row 1: Name + Status */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1,
          }}
        >
          <Typography variant="h6" fontWeight="600" color="text.primary" noWrap>
            {game.name}
          </Typography>
          <GameStatusBadge game={game} winningTeamName={winningTeamName} />
        </Box>

        {/* Row 2: Team Matchup */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: teamOne?.color || "divider",
                flexShrink: 0,
              }}
            />
            <Typography variant="body1" fontWeight="500" color="text.primary">
              {teamOne?.name || "TBD"}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
            vs
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: teamTwo?.color || "divider",
                flexShrink: 0,
              }}
            />
            <Typography variant="body1" fontWeight="500" color="text.primary">
              {teamTwo?.name || "TBD"}
            </Typography>
          </Box>
        </Box>

        {/* Row 3: Metadata */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            by {game.profiles.name}
          </Typography>

          {game.created_at && (
            <>
              <Typography variant="caption" color="text.disabled">
                &middot;
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(new Date(game.created_at), "MMM d, yyyy")}
              </Typography>
            </>
          )}

          {(game.team_1_niche_category ||
            game.team_2_niche_category ||
            game.bonus_round_type) && (
            <>
              <Typography variant="caption" color="text.disabled">
                &middot;
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {game.team_1_niche_category && (
                  <Chip
                    label={game.team_1_niche_category}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                )}
                {game.team_2_niche_category && (
                  <Chip
                    label={game.team_2_niche_category}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                )}
                {game.bonus_round_type && (
                  <Chip
                    label={game.bonus_round_type}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                )}
              </Stack>
            </>
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ml: 3,
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {primaryAction()}

        {!game.is_started && game.config_complete && userIsCreator && (
          <StandardButton
            variant="outlined"
            startIcon={<EditOutlined />}
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              nav(`/game/${game.id}/builder`);
            }}
          >
            Edit
          </StandardButton>
        )}

        <Tooltip title="Copy Game Link">
          <StandardButton
            variant="outlined"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              onCopyLink();
            }}
            sx={{ minWidth: "auto", px: 1 }}
          >
            <CopyAll fontSize="small" />
          </StandardButton>
        </Tooltip>

        {userIsCreator && (
          <Tooltip title="Delete Game">
            <StandardButton
              variant="outlined"
              color="error"
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{ minWidth: "auto", px: 1 }}
            >
              <Delete fontSize="small" />
            </StandardButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default GameListItem;
